export const PUBLIC_METAL_QUOTE_UNIT = 'IRR_PER_GRAM';
export const PUBLIC_METAL_IDS = ['silver', 'gold_18k', 'copper'];
export const PUBLIC_METAL_SOURCES = {
  silver: {id: 'qolak-public-silver', url: 'https://app.mokaab.gold/api/v1/prices', label: 'نرخ عمومی قلک', basis: 'published_silver_rial_per_gram'},
  gold_18k: {id: 'mokaab-public-gold18', url: 'https://mokaab.gold/api/market-quotes', label: 'نرخ عمومی طلای ۱۸ عیار مکعب', basis: 'published_gold18_toman_per_gram'},
  copper: {id: 'qolak-owner-copper', url: null, label: 'نرخ مس تأییدشده توسط مالک', basis: 'owner_approved_toman_per_kilogram'},
};

export const ADMIN_COPPER_SOURCE = {id:'qolak-admin-copper',url:'https://app.mokaab.gold/api/v1/copper-price',label:'نرخ مس ثبت‌شده توسط ادمین مکعب',basis:'published_copper_rial_per_gram'};
const TIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})?$/;
const REASONS = /^[A-Za-z0-9_:-]{1,100}$/;
const SUPPORT = {status: 'illustrative_only', purchase: false, sale: false, purpose: 'illustration'};
const object = value => value && typeof value === 'object' && !Array.isArray(value);

function validTime(value, zoned = false) {
  if (typeof value !== 'string') return false;
  const parts = TIME.exec(value);
  if (!parts || (zoned && (!parts[7] || value[10] !== 'T'))) return false;
  const [, year, month, day, hour, minute, second, zone] = parts;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.getUTCFullYear() === Number(year) && date.getUTCMonth() === Number(month) - 1
    && date.getUTCDate() === Number(day) && Number(hour) < 24 && Number(minute) < 60 && Number(second) < 60
    && (!zone || zone === 'Z' || (Number(zone.slice(1, 3)) < 24 && Number(zone.slice(4)) < 60))
    && (!zoned || Number.isFinite(Date.parse(value)));
}

// A kilogram price in whole toman can result in hundredths of a rial per gram.
// Keep that reference precision; do not round it into a settlement price.
const validRate = value => typeof value === 'number' && Number.isFinite(value) && value > 0
  && value <= Number.MAX_SAFE_INTEGER / 100 && Number(value.toFixed(2)) === value;

export function unavailableMetalQuote(metal, reason = 'PUBLIC_QUOTE_UNAVAILABLE', checkedAt = null) {
  return {
    metal, sellPriceRial: null, buyPriceRial: null, unit: PUBLIC_METAL_QUOTE_UNIT,
    publishedAt: null, checkedAt, connection: 'unavailable', sourceFreshness: 'unknown',
    source: {...PUBLIC_METAL_SOURCES[metal]}, support: {...SUPPORT}, reason,
  };
}

export function initialPublicMetalQuotes() {
  return Object.fromEntries(PUBLIC_METAL_IDS.map(metal => [metal, unavailableMetalQuote(metal, 'NOT_CHECKED')]));
}

export function normalizePublicMetalQuote(payload, metal) {
  const source=metal==='copper'&&payload?.source?.id===ADMIN_COPPER_SOURCE.id?ADMIN_COPPER_SOURCE:PUBLIC_METAL_SOURCES[metal];
  if (!PUBLIC_METAL_IDS.includes(metal) || !object(payload) || payload.metal !== metal
    || payload.unit !== PUBLIC_METAL_QUOTE_UNIT || !['current', 'retained', 'unavailable'].includes(payload.connection)
    || !['fresh', 'stale', 'unknown'].includes(payload.sourceFreshness)
    || !object(payload.source) || !Object.entries(source).every(([key, value]) => payload.source[key] === value)
    || !object(payload.support) || !Object.entries(SUPPORT).every(([key, value]) => payload.support[key] === value)
    || !(payload.reason === null || (typeof payload.reason === 'string' && REASONS.test(payload.reason)))) return null;
  if (payload.connection === 'unavailable') {
    if (payload.sellPriceRial !== null || payload.buyPriceRial !== null || payload.publishedAt !== null
      || !(payload.checkedAt === null || validTime(payload.checkedAt, true)) || !payload.reason) return null;
  } else if (!validRate(payload.sellPriceRial)
    || !(payload.buyPriceRial === null || validRate(payload.buyPriceRial))
    || (metal === 'silver' && !validRate(payload.buyPriceRial))
    || !validTime(payload.publishedAt) || !validTime(payload.checkedAt, true)) return null;
  return {
    metal, sellPriceRial: payload.sellPriceRial, buyPriceRial: payload.buyPriceRial, unit: PUBLIC_METAL_QUOTE_UNIT,
    publishedAt: payload.publishedAt, checkedAt: payload.checkedAt, connection: payload.connection,
    sourceFreshness: payload.sourceFreshness, source: {...source}, support: {...SUPPORT}, reason: payload.reason,
  };
}

export function normalizePublicMetalQuotes(payload) {
  if (!object(payload) || payload.schemaVersion !== 1 || !object(payload.quotes)) return null;
  return Object.fromEntries(PUBLIC_METAL_IDS.map(metal => [metal,
    normalizePublicMetalQuote(payload.quotes[metal], metal) || unavailableMetalQuote(metal, 'INVALID_PUBLIC_QUOTE'),
  ]));
}

export function retainPublicMetalQuotes(previous, incoming = null, reason = 'PUBLIC_SOURCE_UNAVAILABLE') {
  return Object.fromEntries(PUBLIC_METAL_IDS.map(metal => {
    const next = incoming?.[metal];
    const prior = normalizePublicMetalQuote(previous?.[metal], metal);
    if (next && next.connection !== 'unavailable') return [metal, next];
    if (prior && prior.connection !== 'unavailable') return [metal, {...prior, connection: 'retained', reason: next?.reason || reason}];
    return [metal, next || unavailableMetalQuote(metal, reason)];
  }));
}
